// src/components/StaffOverview.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import neo4jService from '../services/neo4jService';
import { departmentQueries, staffQueries } from '../repository/graphQueries';
import { formatDate } from '../utils';

const StaffOverview = () => {
    const [departments, setDepartments] = useState([]);
    const [staffData, setStaffData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Get context
    let contextNeo4jService;
    try {
        const context = useOutletContext();
        contextNeo4jService = context?.neo4jService;
    } catch (e) {
        console.warn("useOutletContext not available for StaffOverview");
    }
    const activeNeo4jService = contextNeo4jService || neo4jService;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (!activeNeo4jService.driver) {
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                await activeNeo4jService.initialize(uri, user, pass);
            }

            // Fetch departments and staff data
            const [deptResult, staffResult] = await Promise.all([
                activeNeo4jService.runQuery(departmentQueries.listAllDepartments),
                activeNeo4jService.runQuery(staffQueries.getStaffPerformanceSummaryList)
            ]);

            setDepartments(deptResult.map(record => ({ name: record.department })));
            setStaffData(staffResult.map(record => ({
                id: record.staffId,
                name: record.staffName,
                department: record.department,
                trailingScore: record.trailingScore,
                imageUrl: record.staffImageUrl
            })));
        } catch (err) {
            console.error("Error fetching overview data:", err);
            setError("Failed to load data. " + err.message);
        } finally {
            setLoading(false);
        }
    }, [activeNeo4jService]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    if (loading) {
        return <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
            <span className="ml-3">Loading overview...</span>
        </div>;
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md">
            <strong>Error:</strong> {error} 
            <button onClick={fetchData} className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm">Retry</button>
        </div>;
    }

    return (
        <div className="container mx-auto p-4">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Staff Dashboard</h1>
                <p className="text-gray-600">Overview of staff performance and department data</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Staff</p>
                            <p className="text-2xl font-semibold text-gray-900">{staffData.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Departments</p>
                            <p className="text-2xl font-semibold text-gray-900">{departments.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Avg Score</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {(() => {
                                    const validScores = staffData.filter(s => s.trailingScore !== null && s.trailingScore !== undefined);
                                    return validScores.length > 0 
                                        ? (validScores.reduce((sum, staff) => sum + staff.trailingScore, 0) / validScores.length).toFixed(2)
                                        : 'N/A';
                                })()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Department Cards */}
            <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Departments</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {departments.map((dept, index) => {
                        const deptStaff = staffData.filter(staff => staff.department === dept.name);
                        const validDeptScores = deptStaff.filter(s => s.trailingScore !== null && s.trailingScore !== undefined);
                        const avgScore = validDeptScores.length > 0 
                            ? (validDeptScores.reduce((sum, staff) => sum + staff.trailingScore, 0) / validDeptScores.length).toFixed(2)
                            : 'N/A';
                        
                        return (
                            <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-800">{dept.name}</h3>
                                    <span className="text-sm text-gray-500">{deptStaff.length} staff</span>
                                </div>
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-1">Average Score</p>
                                    <p className="text-2xl font-bold text-blue-600">{avgScore}</p>
                                </div>
                                <Link 
                                    to={`/internal/${encodeURIComponent(dept.name)}/overview`}
                                    className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    View Department
                                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Staff Performance Table */}
            <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Staff Performance</h2>
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                                <th className="px-5 py-3 border-b-2 border-gray-200">Staff Name</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200">Department</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Trailing Score</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-gray-700">
                            {staffData.slice(0, 10).map((staff) => (
                                <tr key={staff.id} className="border-b border-gray-200 hover:bg-gray-50">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center">
                                            {staff.imageUrl ? (
                                                <img src={staff.imageUrl} alt={staff.name} className="w-10 h-10 rounded-full mr-3 object-cover" 
                                                     onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/40x40/E2E8F0/718096?text=${staff.name.charAt(0)}`; }} />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full mr-3 bg-gray-200 flex items-center justify-center text-gray-500 font-semibold">
                                                    {staff.name ? staff.name.charAt(0) : '?'}
                                                </div>
                                            )}
                                            <span className="font-medium">{staff.name || 'N/A'}</span>
                                        </div>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className="text-gray-600">{staff.department || 'N/A'}</span>
                                    </td>
                                    <td className="px-5 py-4 text-center">
                                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                                            staff.trailingScore === null || staff.trailingScore === undefined ? 'bg-gray-200 text-gray-600' :
                                            staff.trailingScore >= 4 ? 'bg-green-100 text-green-700' :
                                            staff.trailingScore >= 3 ? 'bg-yellow-100 text-yellow-700' :
                                            'bg-red-100 text-red-700'
                                        }`}>
                                            {staff.trailingScore !== null && staff.trailingScore !== undefined ? staff.trailingScore.toFixed(2) : 'N/A'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-center">
                                        <Link 
                                            to={`/staff/${staff.id}`}
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                            View Details
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {staffData.length > 10 && (
                        <div className="p-4 text-center">
                            <Link 
                                to="/staff/list"
                                className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                                View All Staff ({staffData.length} total)
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StaffOverview; 